/**
 * useSupportTickets Hook
 * 
 * Custom hook for managing support tickets data fetching and state.
 * Provides methods for listing tickets, fetching details, and managing replies.
 */

import { useState, useCallback, useEffect } from 'react';
import * as supportService from '../services/supportService';

/**
 * Initialize state for ticket list and filtering
 */
function useTicketList() {
  const [tickets, setTickets] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchTickets = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const result = await supportService.getTickets({
        page: params.page || page,
        pageSize: params.pageSize || pageSize,
        searchText: params.searchText,
        status: params.status,
        priority: params.priority,
      });
      
      setTickets(result.tickets);
      setTotalCount(result.totalCount);
      setPage(result.page);
      setPageSize(result.pageSize);
    } catch (err) {
      setError(err.message || 'Falha ao carregar tickets');
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  return {
    tickets,
    totalCount,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    fetchTickets,
  };
}

/**
 * Initialize state for ticket details and replies
 */
function useTicketDetails() {
  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDetails = useCallback(async (ticketId) => {
    if (!ticketId) {
      setTicket(null);
      setReplies([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await supportService.getTicketDetails(ticketId);
      setTicket(result.ticket);
      setReplies(result.replies);
    } catch (err) {
      setError(err.message || 'Falha ao carregar detalhes do ticket');
      console.error('Error fetching ticket details:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const addReply = useCallback((newReply) => {
    setReplies(prev => [...prev, newReply]);
  }, []);

  const updateReplyReadStatus = useCallback((replyId) => {
    setReplies(prev => 
      prev.map(reply => 
        reply.id === replyId ? { ...reply, readAt: new Date().toISOString() } : reply
      )
    );
  }, []);

  return {
    ticket,
    replies,
    loading,
    error,
    fetchDetails,
    addReply,
    updateReplyReadStatus,
    setTicket,
  };
}

/**
 * Initialize state for reply submission
 */
function useReplySubmit() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = useCallback(async (ticketId, content) => {
    setSubmitting(true);
    setError(null);
    try {
      const newReply = await supportService.createReply(ticketId, content);
      return newReply;
    } catch (err) {
      const errorMsg = err.message || 'Falha ao enviar resposta';
      setError(errorMsg);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, []);

  return {
    submitting,
    error,
    submit,
    setError,
  };
}

/**
 * Initialize state for status updates
 */
function useStatusUpdate() {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  const update = useCallback(async (ticketId, status) => {
    setUpdating(true);
    setError(null);
    try {
      const updatedTicket = await supportService.updateTicketStatus(ticketId, status);
      return updatedTicket;
    } catch (err) {
      const errorMsg = err.message || 'Falha ao atualizar status';
      setError(errorMsg);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  return {
    updating,
    error,
    update,
    setError,
  };
}

/**
 * Main hook: useSupportTickets
 * 
 * Combines all ticket-related state management and operations.
 * Provides complete interface for support tickets admin panel.
 * 
 * @returns {Object} Ticket list, details, and operations
 * 
 * @example
 * const supportTickets = useSupportTickets();
 * 
 * // Fetch initial tickets
 * useEffect(() => {
 *   supportTickets.fetchTickets({});
 * }, []);
 * 
 * // Search and filter
 * supportTickets.fetchTickets({
 *   searchText: 'billing',
 *   status: 0,
 *   priority: 2,
 *   page: 1,
 * });
 * 
 * // Fetch ticket details
 * supportTickets.fetchTicketDetails(ticketId);
 * 
 * // Submit admin reply
 * await supportTickets.submitReply(ticketId, '<p>Thank you...</p>');
 * 
 * // Update status
 * await supportTickets.updateStatus(ticketId, 2);
 */
export function useSupportTickets() {
  const ticketList = useTicketList();
  const ticketDetails = useTicketDetails();
  const replySubmit = useReplySubmit();
  const statusUpdate = useStatusUpdate();

  // Combined fetch method for convenience
  const fetchTickets = useCallback(async (params) => {
    await ticketList.fetchTickets(params);
  }, [ticketList]);

  // Fetch details when ticket is selected
  const fetchTicketDetails = useCallback(async (ticketId) => {
    await ticketDetails.fetchDetails(ticketId);
  }, [ticketDetails]);

  // Submit reply with combined state management
  const submitReply = useCallback(async (ticketId, content) => {
    try {
      const newReply = await replySubmit.submit(ticketId, content);
      
      // Update local state
      ticketDetails.addReply(newReply);
      
      // Refresh ticket list to update reply count
      await ticketList.fetchTickets({
        page: ticketList.page,
        pageSize: ticketList.pageSize,
      });
      
      return newReply;
    } catch (err) {
      throw err;
    }
  }, [ticketList, ticketDetails, replySubmit]);

  // Update status with combined state management
  const updateStatus = useCallback(async (ticketId, newStatus) => {
    try {
      const updatedTicket = await statusUpdate.update(ticketId, newStatus);
      
      // Update local state
      ticketDetails.setTicket(prev => ({
        ...prev,
        status: newStatus,
      }));
      
      // Update in ticket list
      ticketList.setTickets(prev =>
        prev.map(t => t.id === ticketId ? { ...t, status: newStatus } : t)
      );
      
      return updatedTicket;
    } catch (err) {
      throw err;
    }
  }, [ticketList, ticketDetails, statusUpdate]);

  return {
    // Ticket List State
    tickets: ticketList.tickets,
    totalCount: ticketList.totalCount,
    listLoading: ticketList.loading,
    listError: ticketList.error,
    page: ticketList.page,
    pageSize: ticketList.pageSize,
    setPage: ticketList.setPage,
    setPageSize: ticketList.setPageSize,

    // Ticket Details State
    selectedTicket: ticketDetails.ticket,
    replies: ticketDetails.replies,
    ticketDetailsLoading: ticketDetails.loading,
    ticketDetailsError: ticketDetails.error,

    // Reply Submit State
    replySubmitting: replySubmit.submitting,
    replyError: replySubmit.error,

    // Status Update State
    statusUpdating: statusUpdate.updating,
    statusUpdateError: statusUpdate.error,

    // Operations
    fetchTickets,
    fetchTicketDetails,
    submitReply,
    updateStatus,

    // Utility methods
    clearReplyError: () => replySubmit.setError(null),
    clearStatusError: () => statusUpdate.setError(null),
    updateReplyReadStatus: ticketDetails.updateReplyReadStatus,
  };
}

export default useSupportTickets;
